// src/pages/ProvaPage.tsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, ChevronUp, AlertTriangle } from 'lucide-react';
import { examService } from '../services/examService';
import { OfficialExam, UserExamAttempt, ExamAnswer } from '../types/exam.types';
import { Question } from '../types/question.types';
import './ProvaPage.css';

const normalizeStatementHtml = (html: string): string => {
    if (!html) return '';
    return html.replace(
        /<\/(span|b|strong|em|i|u)>\s*<br\s*\/?>\s*<(span|b|strong|em|i|u)/gi,
        '</$1> <$2'
    );
};

const ProvaPage: React.FC = () => {
    const { examId } = useParams<{ examId: string }>();

    const [exam, setExam] = useState<OfficialExam | null>(null);
    const [attempt, setAttempt] = useState<UserExamAttempt | null>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [answers, setAnswers] = useState<Map<string, string>>(new Map());
    const [eliminated, setEliminated] = useState<Map<string, Set<string>>>(new Map());
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [isInterdicted, setIsInterdicted] = useState(false);
    const [finishing, setFinishing] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [autoFinishing, setAutoFinishing] = useState(false);

    // ── Timer ────────────────────────────────────────────────
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const elapsedRef = useRef(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const autoFinishedRef = useRef(false);

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            elapsedRef.current += 1;
            setElapsedSeconds(elapsedRef.current);
        }, 1000);
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, []);

    const stopTimer = useCallback(() => {
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    }, []);

    const questionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const getQuestionId = (questionId: any): string => {
        if (typeof questionId === 'string') return questionId;
        if (questionId?._id) return questionId._id.toString();
        return String(questionId) || '';
    };

    const loadExam = useCallback(async () => {
        try {
            setLoading(true);
            setLoadError('');
            const examData = await examService.getExam(examId!);
            setExam(examData);

            const populatedQuestions: Question[] = examData.questions
                .map((item: any) => {
                    const q = item.questionId;
                    if (q && typeof q === 'object' && q._id) return q as Question;
                    return null;
                })
                .filter(Boolean) as Question[];

            if (populatedQuestions.length === 0) {
                const { questionService } = await import('../services/questionService');
                const fetched = await Promise.all(
                    examData.questions.map((item: any) => {
                        const qId = getQuestionId(item.questionId);
                        return questionService.getQuestion(qId).then((res: any) => res.question).catch(() => null);
                    })
                );
                setQuestions(fetched.filter(Boolean) as Question[]);
            } else {
                setQuestions(populatedQuestions);
            }

            const myAttempts = await examService.getMyAttempts();
            const normalize = (a: UserExamAttempt) => {
                const id = typeof a.examId === 'object' ? (a.examId as any)?._id ?? a.examId : a.examId;
                return String(id);
            };

            const ongoing = myAttempts.find(a => normalize(a) === String(examId) && a.status === 'em_andamento');
            const finished = myAttempts
                .filter(a => normalize(a) === String(examId) && a.status === 'finalizado')
                .sort((a, b) => new Date(b.finishedAt || b.startedAt).getTime() - new Date(a.finishedAt || a.startedAt).getTime())[0];

            const existingAttempt = ongoing || finished;

            if (existingAttempt) {
                setAttempt(existingAttempt);
                const answersMap = new Map<string, string>();
                existingAttempt.answers.forEach((a: any) => {
                    if (a.selectedAlternative) answersMap.set(String(a.questionId), a.selectedAlternative);
                });
                setAnswers(answersMap);
                if (existingAttempt.status === 'finalizado') stopTimer();
            } else {
                const newAttempt = await examService.startExam(examId!);
                setAttempt(newAttempt);
            }
        } catch (err: any) {
            if (err?.interdicted) {
                setIsInterdicted(true);
            } else {
                setLoadError('Não foi possível carregar a prova. Tente fechar e abrir novamente.');
            }
        } finally {
            setLoading(false);
        }
    }, [examId, stopTimer]);

    useEffect(() => { if (examId) loadExam(); }, [examId, loadExam]);

    const executeFinish = useCallback(async (silent = false) => {
        if (!exam || !attempt || attempt.status === 'finalizado') return;
        stopTimer();
        const totalTime = elapsedRef.current;
        try {
            if (!silent) setFinishing(true);
            setShowConfirmModal(false);
            const answersArray: ExamAnswer[] = exam.questions.map((item: any) => {
                const qId = getQuestionId(item.questionId);
                return {
                    questionId: qId,
                    order: item.order ?? item.orderNumber ?? 0,
                    selectedAlternative: (answers.get(qId) as 'A' | 'B' | 'C' | 'D' | 'E') || null,
                    timeSpent: totalTime
                };
            });
            const result = await examService.finishExam(attempt._id, answersArray);
            setAttempt(result);
        } catch {
            setLoadError('Erro ao finalizar prova. Tente novamente.');
        } finally {
            setFinishing(false);
            setAutoFinishing(false);
        }
    }, [exam, attempt, answers, stopTimer]);

    const totalSeconds = exam?.officialDuration ? exam.officialDuration * 60 : null;
    const remaining = totalSeconds !== null ? Math.max(0, totalSeconds - elapsedSeconds) : null;
    const isWarning = remaining !== null && remaining <= 300 && remaining > 0;
    const isCritical = remaining !== null && remaining <= 60 && remaining > 0;

    useEffect(() => {
        if (
            remaining === 0 &&
            attempt?.status === 'em_andamento' &&
            !finishing &&
            !autoFinishedRef.current
        ) {
            autoFinishedRef.current = true;
            setAutoFinishing(true);
            executeFinish(true);
        }
    }, [remaining, attempt?.status, finishing, executeFinish]);

    const handleFinish = useCallback(() => {
        if (!exam || !attempt || finishing || attempt.status === 'finalizado') return;
        setShowConfirmModal(true);
    }, [exam, attempt, finishing]);

    useEffect(() => {
        if (attempt?.status === 'em_andamento') {
            const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
            window.addEventListener('beforeunload', handler);
            return () => window.removeEventListener('beforeunload', handler);
        }
    }, [attempt?.status]);

    useEffect(() => {
        const handleScroll = () => setShowScrollTop(window.scrollY > 600);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleSelectAlternative = (questionId: string, letter: string) => {
        if (attempt?.status === 'finalizado') return;
        setAnswers(prev => { const n = new Map(prev); n.set(questionId, letter); return n; });
    };

    const toggleEliminate = (questionId: string, letter: string) => {
        setEliminated(prev => {
            const m = new Map(prev);
            const s = new Set(m.get(questionId) || []);
            s.has(letter) ? s.delete(letter) : s.add(letter);
            m.set(questionId, s);
            return m;
        });
    };

    const scrollToQuestion = (questionId: string) => {
        const el = questionRefs.current[questionId];
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 120, behavior: 'smooth' });
    };

    const formatTime = (s: number) => {
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
        return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    };

    if (loading) return (
        <div className="simulado-loading">
            <div className="spinner" />
            <p>Carregando prova...</p>
        </div>
    );

    if (isInterdicted) return (
        <div className="simulado-error">
            <div className="prova-interdicted-icon"><AlertTriangle size={32} /></div>
            <h2>Prova temporariamente indisponível</h2>
            <p>Esta prova está em manutenção. Tente novamente mais tarde ou entre em contato com o suporte.</p>
            <button onClick={() => window.location.href = '/dashboard'}>Voltar ao Dashboard</button>
        </div>
    );

    if (loadError) return (
        <div className="simulado-error">
            <AlertTriangle size={40} />
            <p>{loadError}</p>
            <button onClick={() => window.close()}>Fechar</button>
        </div>
    );

    if (!exam || !attempt || !questions.length) return (
        <div className="simulado-error">
            <p>Prova não encontrada</p>
            <button onClick={() => window.close()}>Fechar</button>
        </div>
    );

    const isFinished = attempt.status === 'finalizado';
    const answeredCount = answers.size;

    return (
        <div className="simulado-page">

            {/* ── Header ── */}
            <div className="simulado-header">
                <div className="header-logo">GRAIA</div>

                {!isFinished && (
                    <div className="header-center">
                        <div className={`header-timer ${isCritical ? 'critical' : isWarning ? 'warning' : ''}`}>
                            <Clock size={15} />
                            <span>{remaining !== null ? formatTime(remaining) : formatTime(elapsedSeconds)}</span>
                            {remaining !== null && <span className="timer-label">restantes</span>}
                        </div>
                        <span className="header-answered">{answeredCount}/{questions.length} respondidas</span>
                    </div>
                )}

                {!isFinished && (
                    <button className="btn-finish-header" onClick={handleFinish} disabled={finishing}>
                        {finishing ? 'Finalizando...' : 'Finalizar Prova'}
                    </button>
                )}

                {isFinished && (
                    <button
                        className="btn-back-header"
                        onClick={() => {
                            sessionStorage.setItem('provas_initial_tab', 'history');
                            window.location.href = '/dashboard';
                        }}
                    >
                        Ver Histórico
                    </button>
                )}
            </div>

            {/* ── Mapa de questões + resultado unificado ── */}
            <div className={`simulado-question-map${isFinished ? ' simulado-question-map--finished' : ''}`}>
                {isFinished && (
                    <div className="qmap-finish-info">
                        <div className="qmap-finish-title">
                            <CheckCircle size={18} />
                            <span>Prova concluída — {exam.name}</span>
                        </div>
                        <div className="qmap-finish-stats">
                            <div className="qmap-stat">
                                <span className="qmap-stat-value qmap-stat--ok">{attempt.results.totalCorrect}</span>
                                <span className="qmap-stat-label">Acertos</span>
                            </div>
                            <div className="qmap-stat">
                                <span className="qmap-stat-value qmap-stat--err">{attempt.results.totalWrong}</span>
                                <span className="qmap-stat-label">Erros</span>
                            </div>
                            <div className="qmap-stat">
                                <span className="qmap-stat-value qmap-stat--acc">{attempt.results.accuracy}%</span>
                                <span className="qmap-stat-label">Aproveitamento</span>
                            </div>
                            <div className="qmap-stat">
                                <span className="qmap-stat-value">{formatTime(attempt.results.totalTime)}</span>
                                <span className="qmap-stat-label">Tempo total</span>
                            </div>
                        </div>
                    </div>
                )}
                <div className="question-map-inner">
                    {questions.map((question, index) => {
                        const answered = answers.has(question._id);
                        const savedAnswer = isFinished
                            ? attempt.answers.find((a: any) => String(a.questionId) === question._id)
                            : null;
                        const isCorrect = savedAnswer?.isCorrect;
                        return (
                            <button
                                key={question._id}
                                className={`nav-circle ${answered && !isFinished ? 'answered' : ''} ${isFinished ? (isCorrect ? 'correct' : 'wrong') : ''}`}
                                onClick={() => scrollToQuestion(question._id)}
                                title={`Questão ${index + 1}`}
                            >
                                {index + 1}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Lista de questões ── */}
            <div className="questions-list">
                {questions.map((question, index) => {
                    const userAnswer = answers.get(question._id);
                    const savedAnswer = attempt.answers.find((a: any) => String(a.questionId) === question._id);
                    const isCorrect = savedAnswer?.isCorrect;
                    const eliminatedSet = eliminated.get(question._id) || new Set<string>();
                    return (
                        <div
                            key={question._id}
                            ref={el => { questionRefs.current[question._id] = el; }}
                            className={`question-block ${isFinished ? (isCorrect ? 'correct' : 'wrong') : ''}`}
                        >
                            <div className="question-number">
                                <div className="question-number-left">
                                    <span className="question-num-badge">{index + 1}</span>
                                    {isFinished && (
                                        <span className={`status-badge ${isCorrect ? 'correct' : 'wrong'}`}>
                                            {isCorrect ? <><CheckCircle size={14} /> Correta</> : <><XCircle size={14} /> Errada</>}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {question.statementImage ? (
                                <div className="question-statement">
                                    <div dangerouslySetInnerHTML={{ __html: normalizeStatementHtml(question.statement) }} />
                                    <img src={question.statementImage} alt="Imagem da questão" />
                                </div>
                            ) : (
                                <div
                                    className="question-statement"
                                    dangerouslySetInnerHTML={{ __html: normalizeStatementHtml(question.statement) }}
                                />
                            )}

                            <div className="alternatives-list">
                                {question.alternatives.map(alt => {
                                    const isSelected = userAnswer === alt.letter;
                                    const isCorrectAlt = isFinished && question.correctAlternative === alt.letter;
                                    const isEliminated = !isFinished && eliminatedSet.has(alt.letter);
                                    return (
                                        <div
                                            key={alt.letter}
                                            role="button"
                                            tabIndex={isFinished || isEliminated ? -1 : 0}
                                            className={`alternative-item ${isSelected ? 'selected' : ''} ${isEliminated ? 'eliminated' : ''} ${isFinished ? 'finished' : ''} ${isFinished && isCorrectAlt ? 'correct-answer' : ''} ${isFinished && isSelected && !isCorrectAlt ? 'wrong-answer' : ''}`}
                                            onClick={() => { if (!isEliminated && !isFinished) handleSelectAlternative(question._id, alt.letter); }}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!isEliminated && !isFinished) handleSelectAlternative(question._id, alt.letter); } }}
                                        >
                                            <span className="alt-letter">{alt.letter}</span>
                                            <span className="alt-text" dangerouslySetInnerHTML={{ __html: alt.text }} />
                                            {!isFinished && (
                                                <button
                                                    className="btn-eliminate"
                                                    onClick={e => { e.stopPropagation(); toggleEliminate(question._id, alt.letter); }}
                                                    title={isEliminated ? 'Restaurar alternativa' : 'Eliminar alternativa'}
                                                    tabIndex={-1}
                                                >
                                                    {isEliminated ? '↩' : '✕'}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {isFinished && (
                                <div className="question-resolution">
                                    {!isCorrect && (
                                        <div className="user-answer-info">
                                            <span>Sua resposta: <strong>{userAnswer || 'Não respondida'}</strong></span>
                                            <span>Correta: <strong>{question.correctAlternative}</strong></span>
                                        </div>
                                    )}
                                    {question.explanationText && (
                                        <div className="resolution-text" dangerouslySetInnerHTML={{ __html: question.explanationText }} />
                                    )}
                                    {question.explanationVideoUrl && (
                                        <div className="resolution-buttons">
                                            <a className="btn-resolution active" href={question.explanationVideoUrl} target="_blank" rel="noreferrer">
                                                Ver resolução em vídeo
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {!isFinished && (
                    <div className="simulado-end-actions">
                        <p className="end-reminder">
                            {answeredCount < questions.length
                                ? `Você ainda tem ${questions.length - answeredCount} questão(ões) sem resposta.`
                                : 'Todas as questões foram respondidas.'
                            }
                        </p>
                        <button className="btn-finish-end" onClick={handleFinish} disabled={finishing}>
                            {finishing ? 'Finalizando...' : 'Finalizar Prova'}
                        </button>
                    </div>
                )}
            </div>

            {showScrollTop && (
                <button className="btn-scroll-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    <ChevronUp size={22} />
                </button>
            )}

            {/* ── Modal de confirmação ── */}
            {showConfirmModal && (
                <div className="confirm-modal-overlay" onClick={() => setShowConfirmModal(false)}>
                    <div className="confirm-modal" onClick={e => e.stopPropagation()}>
                        <h3>Finalizar Prova</h3>
                        <p>
                            {answeredCount < questions.length
                                ? `Você ainda tem ${questions.length - answeredCount} questão(ões) sem resposta. Tem certeza que deseja finalizar?`
                                : 'Tem certeza que deseja finalizar? Você não poderá alterar suas respostas.'
                            }
                        </p>
                        <div className="confirm-modal-actions">
                            <button className="confirm-modal-cancel" onClick={() => setShowConfirmModal(false)}>
                                Continuar
                            </button>
                            <button className="confirm-modal-ok" onClick={() => executeFinish()}>
                                Finalizar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {autoFinishing && (
                <div className="confirm-modal-overlay">
                    <div className="confirm-modal">
                        <h3>Tempo encerrado</h3>
                        <p>O tempo da prova acabou. Finalizando e salvando suas respostas...</p>
                        <div className="confirm-modal-loading">
                            <div className="spinner" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProvaPage;
